import React from "react";

import firebase from "js/firebase";
import logger from "js/core/logger";
import Api from "js/core/api";
import * as analytics from "js/analytics";
import { isElectron } from "js/config";
import { CreateAccount, ResetPassword, SignIn, ResetDone, WaitForGoogle } from "./Auth";
import { FirebaseAuthConsumer } from "./FirebaseAuthContext";
import { FirebaseAuthInitalizer } from "./FirebaseAuthInitalizer";
import { GoogleAuthInitalizer } from "./GoogleAuthInitalizer";
import { GoogleAuthConsumer } from "./GoogleAuthContext";
import { AuthSanitizer } from "./AuthSanitizer";

const log = logger.info(logger.AUTH).bind(logger);

export class Authenticate extends React.Component {
    constructor(props) {
        super(props);

        if (props.defaultPage != "signIn" && props.defaultPage != "createAccount") {
            throw new Error(
                "<Authenticate> `defaultPage` prop is required " +
                "(must be `signIn` or `createAccount`)"
            );
        }

        this.state = {
            loading: false,
            error: undefined,
            page: props.defaultPage || "signIn",
            email: "",
        };
    }

    handleSignIn = auth => (async ({ email, password }) => {
        try {
            this.setState({ loading: true, error: undefined, emailError: undefined, passwordError: undefined });

            if (email == "") {
                this.setState({ loading: false, emailError: "Required" });
                return;
            }

            // clicking sign-in an email but no password begins SSO (got this idea from Dropbox)
            if (!!email && password === "") {
                try {
                    log("Checking if SSO is enabled for", email);
                    const status = await Api.ssoStatus.get({ email });
                    if (status.ssoEnabled) {
                        log("SSO is enabled, will redirect");
                        window.location = `/api/sso/saml/login?email=${encodeURIComponent(email)}`;
                        return;
                    }
                    log("SSO is not enabled for", email);
                } catch (err) {
                    this.setState({ loading: false, error: err.message });
                    return;
                }
            }

            const userCredential = await auth.signInWithEmailAndPassword(email, password);
            log("Firebase: signInWithEmailAndPassword ->", userCredential);

            this.setState({ loading: false });
            this.trackLogin(userCredential.user);
        } catch (err) {
            if (err.code == "auth/invalid-email") {
                this.setState({ loading: false, emailError: "Invalid email" });
            } else if (err.code == "auth/user-not-found") {
                this.setState({ loading: false, emailError: "No account for this email" });
            } else if (err.code == "auth/wrong-password") {
                this.setState({ loading: false, passwordError: password == "" ? "Required" : "Incorrect password" });
            } else {
                this.setState({ loading: false, error: err.message });
            }
        }
    })

    // note: for auth with Google, logging in and creating an account are the same thing
    handleGoogle = (auth, auth2) => (async () => {
        if (!auth2.signIn) {
            this.setState({ error: "Google Sign-in failed to load" });
            return;
        }
        const startPage = this.state.page;
        let googleUser = null;
        try {
            this.setState({ page: "waitForGoogle", loading: true, error: undefined, isSigningInToGoogle: true });
            googleUser = await auth2.signIn();
            log("Signed-in to Google as", googleUser);
            this.setState({ page: startPage });
        } catch (err) {
            if (err.code == "auth/popup-closed-by-user") {
                this.setState({ page: startPage, isSigningInToGoogle: false });
                log("Google sign-in cancelled", err);
            } else {
                this.setState({ page: startPage, error: err.message, isSigningInToGoogle: false });
            }
        }

        if (!auth2.isSignedIn.get()) {
            this.setState({ loading: false, isSigningInToGoogle: false });
            return;
        }

        const response = googleUser.getAuthResponse();
        log("Google authResponse:", response);

        const credential = firebase.auth.GoogleAuthProvider.credential(response.id_token);

        log("Signing in to Firebase with Google auth credential");
        const userCredential = await auth.signInWithCredential(credential);
        log("Signed-in to Firebase, result:", userCredential);

        // first sign-in results in account creation
        if (userCredential.additionalUserInfo.isNewUser) {
            log("Google user is new, calling registerUser");
            const results = await Api.registerUser.post();
            log("api/registerUser ->", results);
            this.trackCreateAccount(userCredential.user);
        } else {
            this.trackLogin(userCredential.user);
        }

        this.setState({ loading: false, isSigningInToGoogle: false });
    })

    handleCreateAccount = auth => (async ({ email, password }) => {
        try {
            this.setState({ loading: true, error: undefined });

            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            log("Firebase: createUserWithEmailAndPassword ->", userCredential);

            const results = await Api.registerUser.post();
            log("api/registerUser ->", results);

            this.setState({ loading: false });
            this.trackCreateAccount(userCredential.user);
        } catch (err) {
            this.setState({ loading: false, error: err.message });
        }
    })

    trackCreateAccount = user => {
        analytics.track({
            audit: true,
            category: "Auth",
            action: "signup",
            gtag: "conversion",
            props: {
                email: user.email,
                userId: user.uid,
                //TODO passing in one orgId for this function does not make sense since a user can be part of multiple orgs
                //need to address this in the future.
                workspace_id: "personal"
            }
        });
    }

    trackLogin = user => {
        analytics.track({
            audit: true,
            category: "Auth",
            action: "login",
            props: {
                email: user.email,
                userId: user.uid,
                //TODO passing in one orgId for this function does not make sense since a user can be part of multiple orgs
                //need to address this in the future.
                workspace_id: "personal"
            }
        });
    }

    handleSwitchForm = ({ signIn }) => {
        this.setState({
            page: signIn ? "signIn" : "createAccount",
            error: undefined,
            emailError: undefined,
            passwordError: undefined
        });
    }

    handleClickForgot = ({ email }) => {
        this.setState({ page: "forgotPassword", email });
    }

    handleResetPassword = auth => async ({ email }) => {
        try {
            await auth.sendPasswordResetEmail(email);
            this.setState({ page: "resetDone" });
        } catch (err) {
            this.setState({ emailError: err.message });
        }
    }

    render() {
        return (
            <GoogleAuthInitalizer>
                <FirebaseAuthInitalizer>
                    <GoogleAuthConsumer>
                        {auth2 => (
                            <FirebaseAuthConsumer>
                                {auth => (
                                    <AuthSanitizer
                                        auth={auth}
                                        auth2={auth2}
                                        ignore={this.state.isSigningInToGoogle}
                                    >
                                        {auth.currentUser && !this.state.loading
                                            ? this.props.children
                                            : this.renderAuth(auth, auth2)
                                        }
                                    </AuthSanitizer>
                                )}
                            </FirebaseAuthConsumer>
                        )}
                    </GoogleAuthConsumer>
                </FirebaseAuthInitalizer>
            </GoogleAuthInitalizer>
        );
    }

    renderAuth(auth, auth2) {
        const error = this.state.error || (auth2.error ? "Google Sign-in is disabled: " + auth2.error.details : "");
        switch (this.state.page) {
            case "signIn":
                return (
                    <SignIn
                        loading={this.state.loading}
                        error={error}
                        emailError={this.state.emailError}
                        passwordError={this.state.passwordError}
                        email={this.props.email}
                        showOnlyGoogle={this.props.showOnlyGoogle}
                        onClickSubmit={this.handleSignIn(auth)}
                        onClickGoogleButton={this.handleGoogle(auth, auth2)}
                        onClickForgot={this.handleClickForgot}
                        onClickSwitchLink={this.handleSwitchForm}
                        noToggle={isElectron || !!this.props.email || this.props.noToggle}
                    />
                );
            case "createAccount":
                return (
                    <CreateAccount
                        loading={this.state.loading}
                        error={error}
                        email={this.props.email}
                        showOnlyGoogle={this.props.showOnlyGoogle}
                        onClickSubmit={this.handleCreateAccount(auth)}
                        onClickGoogleButton={this.handleGoogle(auth, auth2)}
                        onClickSwitchLink={this.handleSwitchForm}
                        noToggle={!!this.props.email || this.props.noToggle}
                    />
                );
            case "forgotPassword":
                return (
                    <ResetPassword
                        defaultEmail={this.state.email}
                        onClickReset={this.handleResetPassword(auth)}
                        emailError={this.state.emailError}
                    />
                );
            case "resetDone":
                return (
                    <ResetDone />
                );
            case "waitForGoogle":
                return (
                    <WaitForGoogle />
                );
            default:
                throw new Error();
        }
    }
}
